import { observer } from 'owa-mobx-react';

import { AttachmentCompactView, SpecialDocViewMode } from 'owa-attachment-compact-view';
import loc, { format } from 'owa-localize';
import {
    getListViewDimensions,
    getListViewColumnWidths,
    shouldRenderColumnHeaders,
    getStore as getMailListLayoutStore,
} from 'owa-mail-layout';
import type AttachmentPreviewWellView from 'owa-mail-list-store/lib/store/schema/AttachmentPreviewWellView';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import { getDensityModeString } from 'owa-fabric-theme';
import React from 'react';
import { plusMoreInlinePreviews } from './MailListItemRichPreviewWell.locstring.json';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import classNames from 'owa-classnames';

import {
    wellHeightFull,
    wellHeightMediumOrCompact,
    oneRowAttachmentPreviews,
    oneRowAttachmentPreviewsMedium,
    oneRowAttachmentPreviewsCompact,
    shortWellHeightFull,
    shortWellHeightMediumOrCompact,
    shortWellLineHeightFull,
    shortWellLineHeightMediumOrCompact,
    plusMoreNumber,
} from './MailListItemRichPreviewWell.scss';

import {
    inlinePreviewsSingleLine,
    inlinePreviewsSingleLineItemViewMedium,
    inlinePreviewsSingleLineConversationViewMedium,
    inlinePreviewsSingleLineFullOrCompact,
    inlinePreviewsThreeColumn,
    inlinePreviewsThreeColumnFull,
    inlinePreviewsThreeColumnMediumOrCompact,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';

import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';

const LV_LEFT_RIGHT_PADDING: number = 20; // list view left and right padding when in 3 column
const WIDTH_PERSONA_ICON: number = 48; // width of the persona icon div in 3 column
const SCROLL_BAR_WIDTH: number = 18; // custom scroll bar width
const WIDTH_ALLOC_FOR_DOC: number = 136; // 132: width + 4: margin-right
const WIDTH_ALLOC_FOR_IMAGE: number = 68; // 64: width + 4: margin-right
const WIDTH_OF_PLUS_MORE_DIV: number = 34; // This needs to be same as width for plus more div in scss file

export interface MailListItemRichPreviewWellProps {
    rowId: string;
    isWideView: boolean;
    onPreviewClick: (ev: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => void;
    tableViewId: string;
}

export interface PreviewWellViewState {
    shouldShowPlusMore: boolean;
    numDocToShow: number;
    numImageToShow: number;
}

const defaultExport: React.FunctionComponent<MailListItemRichPreviewWellProps> = observer(
    function MailListItemRichPreviewWell(props: MailListItemRichPreviewWellProps) {
        const densityModeString = getDensityModeString();
        const isFullDensity = densityModeString === 'full';
        const majorMessageListTextStyle = useMessageListTextStyle('Major');

        const renderAttachment = (
            attachmentViewStateId: string,
            isImage: boolean,
            onlyDocsInView: boolean
        ): JSX.Element | null => {
            const attachmentViewState =
                getListViewStore().attachmentViewStates.get(attachmentViewStateId);
            if (!attachmentViewState) {
                return null;
            }
            return (
                <AttachmentCompactView
                    onPreviewClick={props.onPreviewClick}
                    key={attachmentViewState.attachmentId.Id}
                    attachmentViewState={attachmentViewState}
                    isImage={isImage}
                    specialDocViewmode={
                        onlyDocsInView ? SpecialDocViewMode.shortDocView : SpecialDocViewMode.none
                    }
                    attachmentViewStateId={attachmentViewStateId}
                    shouldHideFileSize={true}
                />
            );
        };
        const renderAttachments = (
            isImage: boolean,
            attachmentViewStateIds: string[],
            numberToRender: number,
            onlyDocsInView: boolean
        ): JSX.Element[] => {
            const renderedAttachments: JSX.Element[] = [];
            for (let i = 0; i < numberToRender; i++) {
                const attachment = renderAttachment(
                    attachmentViewStateIds[i],
                    isImage,
                    onlyDocsInView
                );
                if (attachment) {
                    renderedAttachments.push(attachment);
                }
            }
            return renderedAttachments;
        };
        /**
         * Calculate the number of previews that can be shown and whether to show the plus more div
         * @param numOfDocs - Number of doc present
         * @param numOfImages - Number of images present
         * @param containerWidth - the width available
         * @return The preview view state
         */
        const calculatePreviewWellViewState = (
            numOfDocs: number,
            numOfImages: number,
            containerWidth: number
        ): PreviewWellViewState => {
            // start empty
            const previewViewState: PreviewWellViewState = {
                shouldShowPlusMore: false,
                numDocToShow: 0,
                numImageToShow: 0,
            };
            let availableContainerWidth = containerWidth;
            // The total width required to accommodate the previews
            const totalRequiredWidth =
                numOfDocs * WIDTH_ALLOC_FOR_DOC + numOfImages * WIDTH_ALLOC_FOR_IMAGE;
            // We show plus more when we do not have enough space to accommodate the available previews.
            previewViewState.shouldShowPlusMore = totalRequiredWidth > availableContainerWidth;
            // Early return if we are not showing plus more
            if (!previewViewState.shouldShowPlusMore) {
                return {
                    shouldShowPlusMore: false,
                    numDocToShow: numOfDocs,
                    numImageToShow: numOfImages,
                };
            }
            // If we show plus more then we have to allocate the space for it and hence the container width available should be reduced by the
            // space taken by the plus more div
            availableContainerWidth -= WIDTH_OF_PLUS_MORE_DIV;
            // calculate number of documents to show
            const numOfDocsThatCanFit = Math.floor(availableContainerWidth / WIDTH_ALLOC_FOR_DOC);
            previewViewState.numDocToShow = Math.min(numOfDocs, numOfDocsThatCanFit);
            availableContainerWidth =
                availableContainerWidth - previewViewState.numDocToShow * WIDTH_ALLOC_FOR_DOC;
            // calculate number of images to show
            const numOfImagesThatCanFit = Math.floor(
                availableContainerWidth / WIDTH_ALLOC_FOR_IMAGE
            );
            previewViewState.numImageToShow = Math.min(numOfImages, numOfImagesThatCanFit);
            return previewViewState;
        };
        /**
         * Calculates the available container width
         */
        const getContainerWidth = (): number => {
            let containerWidth = 0;
            if (shouldRenderColumnHeaders(props.tableViewId)) {
                containerWidth =
                    (getMailListLayoutStore().subjectColumnWidth ?? 0) - 20 /* Left Padding */;
            } else if (props.isWideView) {
                containerWidth = 500; // VSO-13179 - SLV, how to get container width and pass it before render.
            } else {
                const { listViewWidth } = getListViewDimensions();
                containerWidth =
                    listViewWidth - LV_LEFT_RIGHT_PADDING - WIDTH_PERSONA_ICON - SCROLL_BAR_WIDTH;
            }
            return containerWidth;
        };
        const renderPreviewWell = (
            attachmentPreviewWellViewState: AttachmentPreviewWellView
        ): JSX.Element => {
            let nonEmptyPreviewContainerClasses = classNames(
                isFullDensity ? wellHeightFull : wellHeightMediumOrCompact,
                [
                    'disableTextSelection',
                    oneRowAttachmentPreviews,
                    densityModeString === 'medium' && oneRowAttachmentPreviewsMedium,
                    densityModeString === 'compact' && oneRowAttachmentPreviewsCompact,
                ]
            );
            const numOfDocs = attachmentPreviewWellViewState.documentViewStateIds.length;
            const numOfImages = attachmentPreviewWellViewState.imageViewStateIds.length;
            const totalNumberOfAttachmentPreviews = numOfDocs + numOfImages;
            // Calculate the number of previews that can be shown and whether to show the plus more div
            const previewViewState: PreviewWellViewState = calculatePreviewWellViewState(
                numOfDocs,
                numOfImages,
                getContainerWidth()
            );
            const onlyDocsInView = previewViewState.numImageToShow === 0;
            if (onlyDocsInView) {
                nonEmptyPreviewContainerClasses = classNames(
                    isFullDensity ? shortWellHeightFull : shortWellHeightMediumOrCompact,
                    nonEmptyPreviewContainerClasses
                );
            }

            return (
                <div className={nonEmptyPreviewContainerClasses}>
                    {renderAttachments(
                        false /* isImage */,
                        attachmentPreviewWellViewState.documentViewStateIds,
                        previewViewState.numDocToShow,
                        onlyDocsInView
                    )}
                    {renderAttachments(
                        true /* isImage */,
                        attachmentPreviewWellViewState.imageViewStateIds,
                        previewViewState.numImageToShow,
                        onlyDocsInView
                    )}
                    {previewViewState.shouldShowPlusMore && (
                        <div
                            className={classNames(
                                majorMessageListTextStyle,
                                onlyDocsInView &&
                                    (isFullDensity
                                        ? classNames(shortWellHeightFull, shortWellLineHeightFull)
                                        : classNames(
                                              shortWellHeightMediumOrCompact,
                                              shortWellLineHeightMediumOrCompact
                                          )),
                                plusMoreNumber
                            )}
                        >
                            {format(
                                loc(plusMoreInlinePreviews),
                                totalNumberOfAttachmentPreviews -
                                    previewViewState.numDocToShow -
                                    previewViewState.numImageToShow
                            )}
                        </div>
                    )}
                </div>
            );
        };
        const { isWideView, rowId, tableViewId } = props;
        const attachmentPreviewWellViewState =
            getListViewStore().rowAttachmentPreviewWellViews.get(rowId);
        const isItemView =
            getListViewStore().tableViews.get(tableViewId)?.tableQuery?.listViewType === 1;
        const isMediumDensity = densityModeString === 'medium';

        const containerDivClasses = classNames(
            'disableTextSelection',
            isWideView
                ? classNames(
                      inlinePreviewsSingleLine,
                      isMediumDensity && isItemView && inlinePreviewsSingleLineItemViewMedium,
                      isMediumDensity &&
                          !isItemView &&
                          inlinePreviewsSingleLineConversationViewMedium,
                      !isMediumDensity && inlinePreviewsSingleLineFullOrCompact
                  )
                : classNames(
                      inlinePreviewsThreeColumn,
                      isFullDensity
                          ? inlinePreviewsThreeColumnFull
                          : inlinePreviewsThreeColumnMediumOrCompact
                  )
        );

        const previewWellContainerStyle = React.useCallback(() => {
            return {
                marginLeft: getListViewColumnWidths().senderColumnWidth,
            };
        }, [props.tableViewId]);

        if (attachmentPreviewWellViewState) {
            return (
                <div
                    className={containerDivClasses}
                    style={isWideView ? previewWellContainerStyle() : undefined}
                >
                    {renderPreviewWell(attachmentPreviewWellViewState)}
                </div>
            );
        }

        return null;
    },
    'MailListItemRichPreviewWell'
);
export default defaultExport;
